import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './SelectorColumnas.css';

const reorderArray = (arr, fromIndex, toIndex) => {
  const a = [...arr];
  const [v] = a.splice(fromIndex, 1);
  a.splice(toIndex, 0, v);
  return a;
};

/**
 * Selector de columnas visibles. Dropdown renderizado con Portal en body para evitar quedar detrás de la tabla.
 */
export function SelectorColumnas({
  columnas,
  visibleIds,
  onToggle,
  onRestaurar,
  onSave,
  ordenColumnas,
  onOrdenChange,
  titulo = 'Columnas visibles',
  labelBoton = 'Columnas',
}) {
  const [abierto, setAbierto] = useState(false);
  const [localVisibleIds, setLocalVisibleIds] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [dropdownRect, setDropdownRect] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (abierto) setLocalVisibleIds([...visibleIds]);
  }, [abierto, visibleIds]);

  const orderedColumnas = (() => {
    if (!ordenColumnas || ordenColumnas.length === 0) return columnas;
    const ordered = ordenColumnas.map(id => columnas.find(c => c.id === id)).filter(Boolean);
    const missing = columnas.filter(c => !ordenColumnas.includes(c.id));
    return ordered.concat(missing);
  })();

  const updatePosition = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setDropdownRect({ top: rect.bottom + 4, left: rect.left });
    }
  };

  useEffect(() => {
    if (abierto) {
      updatePosition();
      const onScrollOrResize = () => updatePosition();
      window.addEventListener('scroll', onScrollOrResize, true);
      window.addEventListener('resize', onScrollOrResize);
      return () => {
        window.removeEventListener('scroll', onScrollOrResize, true);
        window.removeEventListener('resize', onScrollOrResize);
      };
    } else {
      setDropdownRect(null);
    }
  }, [abierto]);

  const buildSnapshot = () => {
    const columnasVisibles = {};
    columnas.forEach((col) => {
      columnasVisibles[col.id] = localVisibleIds.includes(col.id);
    });
    const orden =
      ordenColumnas && ordenColumnas.length > 0
        ? [...ordenColumnas]
        : orderedColumnas.map((c) => c.id);
    return { columnasVisibles, ordenColumnas: orden };
  };

  const syncParentFromLocal = () => {
    orderedColumnas.forEach((col) => {
      const inLocal = localVisibleIds.includes(col.id);
      const inParent = visibleIds.includes(col.id);
      if (inLocal !== inParent) onToggle(col.id);
    });
  };

  const handleCerrar = () => {
    syncParentFromLocal();
    const snapshot = buildSnapshot();
    setAbierto(false);
    if (onSave) onSave(snapshot);
  };

  const handleLocalToggle = (id) => {
    setLocalVisibleIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const handleToggleBoton = () => {
    if (abierto) {
      syncParentFromLocal();
      if (onSave) onSave(buildSnapshot());
    }
    setAbierto((o) => !o);
  };

  const handleRestaurar = () => {
    onRestaurar();
    setAbierto(false);
    if (onSave) onSave();
  };

  const handleDragStart = (e, index) => {
    setDragIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    if (dragIndex === null || !onOrdenChange) return;
    const orderedIds = orderedColumnas.map(c => c.id);
    const newOrder = reorderArray(orderedIds, dragIndex, dropIndex);
    onOrdenChange(newOrder);
    setDragIndex(null);
  };

  const handleDragEnd = () => setDragIndex(null);

  const dropdownContent = abierto && dropdownRect && (
    <div
      className="selector-columnas-dropdown"
      role="dialog"
      aria-label={titulo}
      style={{
        position: 'fixed',
        top: dropdownRect.top,
        left: dropdownRect.left,
        width: 'max-content',
        minWidth: 'max-content',
        zIndex: 10000,
      }}
    >
      <div className="selector-columnas-header">
        <span>{titulo}</span>
        <button type="button" className="selector-columnas-restaurar" onClick={handleRestaurar}>
          Ver todas
        </button>
      </div>
      {ordenColumnas && onOrdenChange && (
        <p className="selector-columnas-hint">Arrastrá para cambiar el orden de las columnas.</p>
      )}
      <ul className="selector-columnas-lista">
        {orderedColumnas.map((col, index) => (
          <li
            key={col.id}
            className={dragIndex === index ? 'selector-columnas-li-dragging' : ''}
            draggable={!!(ordenColumnas && onOrdenChange)}
            onDragStart={ordenColumnas && onOrdenChange ? (e) => handleDragStart(e, index) : undefined}
            onDragOver={ordenColumnas && onOrdenChange ? (e) => handleDragOver(e, index) : undefined}
            onDrop={ordenColumnas && onOrdenChange ? (e) => handleDrop(e, index) : undefined}
            onDragEnd={handleDragEnd}
          >
            {ordenColumnas && onOrdenChange && (
              <span className="selector-columnas-drag-handle" title="Arrastrar para reordenar" aria-hidden="true">⋮⋮</span>
            )}
            <label className="selector-columnas-label">
              <input
                type="checkbox"
                checked={localVisibleIds.includes(col.id)}
                onChange={() => handleLocalToggle(col.id)}
              />
              <span>{col.label}</span>
            </label>
          </li>
        ))}
      </ul>
      <div className="selector-columnas-footer">
        <button type="button" className="selector-columnas-cerrar" onClick={handleCerrar}>
          Listo
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="selector-columnas" ref={ref}>
        <button
          type="button"
          className="btn-selector-columnas"
          onClick={handleToggleBoton}
          title={titulo}
          aria-expanded={abierto}
          aria-haspopup="true"
        >
          ⚙️ {labelBoton}
        </button>
      </div>
      {dropdownContent && createPortal(dropdownContent, document.body)}
    </>
  );
}
